/* eslint-disable jsx-a11y/no-onchange */
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'next-i18next'
import classNames from 'classnames'
import { AddressFields, AddressFormProps } from './types'
import { isValidPostalCode } from 'utils/helpers'
import { GetTownsResponse, Towns } from 'types/towns'
import { LettersNumbersSlashesAndDashes } from 'constants/regex'
import { preventArrowUpAndDownEvent, preventInputScrollEvent } from 'utils/eventHandlers'
import { InputGroupKeys, SimulationFieldKeys } from 'store/customer/enums'
import { updateInput } from 'store/customer/slice'
import { useStoreDispatch } from 'hooks/store'
import axios from 'axios'
import { NextApiResponseBody } from 'types/boltApi/request'

const AddressForm = ({
  className,
  errors,
  input,
  isNested,
  productAvailabillityRegions,
  productName,
  register,
  setValue,
  type,
  required = true
}: AddressFormProps) => {
  // Redux store
  const dispatch = useStoreDispatch()

  // i18n
  const { t } = useTranslation(['commonFormFields', 'validation', 'simulation', 'common'])

  // Local state
  const [towns, setTowns] = useState<Towns>([])

  /**
   * Fetches the town names based on the given postalCode
   *
   * @param postalCode
   */
  const validatePostalCodeAndFetchTowns = async (postalCode: number) => {
    if (isValidPostalCode(postalCode)) {
      try {
        // Fetch the towns & the region
        const {
          data: { data }
        } = await axios.get<NextApiResponseBody<GetTownsResponse>>(`/api/towns/${postalCode}`)
        const { towns, region } = data

        setTowns(towns)
        dispatch(updateInput({ group: InputGroupKeys.SIMULATION, key: SimulationFieldKeys.REGION, value: region }))

        if (towns?.length > 0) {
          setValue(getFieldName(AddressFields.TOWN_CODE), towns[0].townCode || 0)
          setValue(getFieldName(AddressFields.TOWN_NAME), towns[0].townName || '')
        }

        // Return error when product is not available in the selected region
        if (productAvailabillityRegions && !productAvailabillityRegions.includes(region)) {
          return t('common:productNotAvailable', '{{product}} is niet beschikbaar in de geselecteerde regio.', {
            product: productName ? `Bolt ${productName}` : t('common:thisProduct', 'Dit product')
          })
        }

        return true
      } catch (error) {
        return t('simulation:invalidPostalCode')
      }
    }

    return t('simulation:invalidPostalCode')
  }

  /**
   * Triggered on first render
   * Checks if town names should be fetched based on postalCode in store
   */
  useEffect(() => {
    const postalCode = input?.postalCode

    if (postalCode !== '') validatePostalCodeAndFetchTowns(parseInt(postalCode, 10))
  }, [])

  /**
   * Returns the css classes for the given field
   *
   * @param {AddressFields} field
   */
  const getFieldClassNames = (field: AddressFields) => {
    return classNames('is-block', {
      'has-error': !isNested ? type in errors && field in errors?.[type] : !!errors?.[field]
    })
  }

  /**
   * Returns the errors for the given field in the current form type
   * @param {AddressFields} field
   * @param {string} attribute
   * @returns {JSX.Element}
   */
  const getFieldErrors = (field: AddressFields, attribute: string): JSX.Element => {
    return !isNested
      ? errors?.[type]?.[field] && (
          <p className="is-error-text">
            {errors?.[type]?.[field]?.type === 'required' && t('validation:required')}
            {errors?.[type]?.[field]?.type === 'maxLength' &&
              t('validation:max.string', {
                attribute,
                length: 5
              })}

            {/* CUSTOM VALIDATION */}
            {field === AddressFields.NUMBER && errors?.[type]?.[field]?.type === 'pattern' && t('validation:address.number.pattern')}
          </p>
        )
      : errors?.[field] && (
          <p className="is-error-text">
            {errors?.[field]?.type === 'required' && t('validation:required')}
            {errors?.[field]?.type === 'maxLength' &&
              t('validation:max.string', {
                attribute,
                length: 5
              })}

            {/* CUSTOM VALIDATION */}
            {field === AddressFields.NUMBER && errors?.[field]?.type === 'pattern' && t('validation:address.number.pattern')}
          </p>
        )
  }

  const getFieldName = useCallback(
    /**
     * Returns the field name based on the given field & current form type
     *
     * @param {AddressFields} field
     */
    (field: AddressFields) => {
      return `${type}.${field}`
    },
    [type]
  )

  /**
   * Handles the town select change & sets the town code
   *
   * @param {string} selectedTown
   */
  const handleTownSelectChange = (selectedTown: string) => {
    const t = towns.find((town) => town.townName === selectedTown)

    setValue(getFieldName(AddressFields.TOWN_CODE), t?.townCode || 0)
    setValue(getFieldName(AddressFields.TOWN_NAME), t?.townName || '')
  }

  useEffect(() => {
    if (towns.length && input?.townName) {
      const selectedTown = towns.find((town) => town.townName === input.townName)
      setValue(getFieldName(AddressFields.TOWN_NAME), (selectedTown ?? towns[0]).townName || '')
      setValue(getFieldName(AddressFields.TOWN_CODE), (selectedTown ?? towns[0]).townCode || 0)
    }
  }, [getFieldName, input?.townName, setValue, towns])

  return (
    <div className={className}>
      <div className="form-row is-2">
        {/* STREET */}
        <div className="form-input">
          <label htmlFor={getFieldName(AddressFields.STREET)} className="mb-100">
            {t('street', 'Straat')}
          </label>
          <input
            id={getFieldName(AddressFields.STREET)}
            type="text"
            className={getFieldClassNames(AddressFields.STREET)}
            {...register(getFieldName(AddressFields.STREET), { required })}
          />
          {getFieldErrors(AddressFields.STREET, t('street', 'Straat'))}
        </div>

        {/* NUMBER */}
        <div className="form-row is-2">
          <div className="form-input">
            <label htmlFor={getFieldName(AddressFields.NUMBER)} className="mb-100">
              {t('number', 'Nummer')}
            </label>
            <input
              id={getFieldName(AddressFields.NUMBER)}
              type="text"
              maxLength={5}
              className={getFieldClassNames(AddressFields.NUMBER)}
              {...register(getFieldName(AddressFields.NUMBER), { maxLength: 5, pattern: LettersNumbersSlashesAndDashes, required })}
            />
            {getFieldErrors(AddressFields.NUMBER, t('number', 'Nummer'))}
          </div>

          {/* BOX */}
          <div className="form-input">
            <label htmlFor={getFieldName(AddressFields.BOX)} className="mb-100">
              {t('box', 'Bus')}
            </label>
            <input
              id={getFieldName(AddressFields.BOX)}
              type="text"
              maxLength={5}
              className={getFieldClassNames(AddressFields.BOX)}
              {...register(getFieldName(AddressFields.BOX), { maxLength: 5 })}
            />
            {getFieldErrors(AddressFields.BOX, t('box', 'Bus'))}
          </div>
        </div>
      </div>

      <div className="form-row is-2">
        {/* POSTAL CODE */}
        <div className="form-input">
          <label htmlFor={getFieldName(AddressFields.POSTAL_CODE)}>{t('zipcode', 'Postcode')}</label>
          <input
            id={getFieldName(AddressFields.POSTAL_CODE)}
            type="number"
            pattern="[0-9]*"
            inputMode="numeric"
            className={getFieldClassNames(AddressFields.POSTAL_CODE)}
            {...register(getFieldName(AddressFields.POSTAL_CODE), {
              required,
              validate: (val) => (required || !!val ? validatePostalCodeAndFetchTowns(parseInt(val as string)) : true)
            })}
            onChange={(e) => validatePostalCodeAndFetchTowns(parseInt(e.target.value, 10))}
            onWheel={(e) => preventInputScrollEvent(e)}
            onKeyDown={(e) => preventArrowUpAndDownEvent(e)}
          />
          {errors?.[type]?.[AddressFields.POSTAL_CODE] && (
            <p className="is-error-text">
              {errors[type][AddressFields.POSTAL_CODE].message || (
                <>{errors[type][AddressFields.POSTAL_CODE].type === 'required' && t('validation:required')}</>
              )}
            </p>
          )}
        </div>

        {/* CITY */}
        <div className="form-input">
          <label htmlFor={getFieldName(AddressFields.TOWN_NAME)}>{t('city', 'Gemeente')}</label>
          <select
            id={getFieldName(AddressFields.TOWN_NAME)}
            className={getFieldClassNames(AddressFields.TOWN_NAME)}
            {...register(getFieldName(AddressFields.TOWN_NAME), { required })}
            onChange={(e) => handleTownSelectChange(e.currentTarget.value)}
          >
            <option disabled>{t('select', 'selecteer...')}</option>
            {towns.map((town, idx) => (
              <option key={`town.geoCode-${idx}`} value={town.townName}>
                {town.townName}
              </option>
            ))}
          </select>
          {getFieldErrors(AddressFields.TOWN_NAME, t('city', 'Gemeente'))}

          <input type="hidden" {...register(getFieldName(AddressFields.TOWN_CODE))} />
        </div>
      </div>
    </div>
  )
}

export default AddressForm
